/**
 * Row 1 of the Channel Deep-dive (spec §3.2, §4): one link per channel in tab
 * order, Facebook, TikTok, Google and LINE Ads always, then a divider and GA4 when
 * the scope has an analytics account (R6). A link carries only the shared keys
 * (R24): switching channel resets type, campaign, Active and tab, because campaign
 * ids belong to one channel. Names come from CHANNEL_LABEL and are not translated.
 *
 * @module components/channels/ChannelTabs
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { CHANNEL_LABEL } from '@/services/channels/channelFormat';
import { AD_CHANNELS, type ChannelPlatform } from '@/services/channels/channelTypes';

interface Props {
  platform: ChannelPlatform;
  showGa4: boolean;
  /** The shared keys (sharedSearchOf), without "?". */
  search: string;
}

const Tab: React.FC<{ platform: ChannelPlatform; current: boolean; search: string }> = ({ platform, current, search }) => (
  <Link
    to={search ? `/channels/${platform}?${search}` : `/channels/${platform}`}
    aria-current={current ? 'page' : undefined}
    className={clsx(
      'inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium transition-colors',
      // Keyboard focus only, with a gap: the current tab is filled ink-950 like the ring, so a ring
      // without one reads as the pill growing 2px. ink-950 stays far above 3:1 on the page's gray-50.
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-950 focus-visible:ring-offset-2',
      current
        ? 'border-ink-950 bg-ink-950 text-white'
        : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:text-ink-900',
    )}
  >
    {CHANNEL_LABEL[platform]}
  </Link>
);

export const ChannelTabs: React.FC<Props> = ({ platform, showGa4, search }) => {
  const { t } = useTranslation();
  return (
    <nav aria-label={t('channels.tabsLabel')} className="flex flex-wrap items-center gap-2">
      {AD_CHANNELS.map((p) => <Tab key={p} platform={p} current={p === platform} search={search} />)}
      {showGa4 && (
        <>
          <span aria-hidden="true" data-tab-divider className="mx-1 h-6 w-px bg-neutral-400" />
          <Tab platform="analytics" current={platform === 'analytics'} search={search} />
        </>
      )}
    </nav>
  );
};
